const EARTH_RADIUS = 6371008.8;

// Límite del mundo en Web Mercator (EPSG:3857) — equivale a ±180° de
// longitud. La vista de OpenLayers puede alejarse lo suficiente para mostrar
// el mundo repetido más de una vez, y `view.calculateExtent()` entonces
// devuelve X fuera de este rango; transformarlo tal cual a EPSG:4326 da
// longitudes inválidas (ej. -189°), que GeoServer/Mongo rechazan con
// "longitude/latitude is out of bounds". Acotar a este límite antes de
// transformar reduce ese caso al mundo completo real, en vez de fallar.
//
// MongoDB además rechaza la longitud/latitud EXACTAMENTE en el límite
// (confirmado empírico: "out of bounds, lng: -180" — no solo más allá de
// -180, el valor -180 mismo) — probablemente por cómo su motor de geometría
// esférica maneja el antimeridiano internamente. Por eso se acota a un
// punto ligeramente adentro del límite (1 metro, imperceptible en cualquier
// escala de mapa), nunca al límite exacto.
const WEB_MERCATOR_WORLD_BOUND = 20037508.342789244 - 1;
export const clampToWebMercatorWorld = ([x, y]) => [
  Math.max(-WEB_MERCATOR_WORLD_BOUND, Math.min(WEB_MERCATOR_WORLD_BOUND, x)),
  Math.max(-WEB_MERCATOR_WORLD_BOUND, Math.min(WEB_MERCATOR_WORLD_BOUND, y)),
];

export const FILTER_VERSION = 2;

export const emptyAppliedFilter = () => ({ version: FILTER_VERSION, mode: "none" });

export const quickAppliedFilter = (quick = {}) => ({
  version: FILTER_VERSION,
  mode: "quick",
  quick: JSON.parse(JSON.stringify(quick || {})),
});

export const newGroup = (depth = 1) => ({
  id: `group-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  kind: "group",
  match: "any",
  depth,
  children: [],
});

export const newCondition = () => ({
  id: `condition-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  kind: "condition",
  field: "",
  // "En uno de" (multiselect buscable) es el método principal de búsqueda —
  // ver AttributeFilter.vue y AttributesTable.vue::filterFor, mismo default.
  operator: "in",
  value: [],
});

export const emptyAdvancedDraft = () => ({
  attribute: { enabled: true, root: newGroup(1) },
  roi: { enabled: true, spec: null },
  crossLayer: {
    enabled: false,
    targetLayerId: null,
    targetLayerLabel: "",
    operation: "intersects",
    applyRoiToTarget: true,
    targetAttribute: { enabled: true, root: newGroup(1) },
    // targetSelectionMode/targetSelection: forma alternativa (excluyente) de
    // acotar las entidades objetivo — en vez de condiciones de atributo, el
    // usuario las elige clickeándolas en el mapa (solo capas de superficie,
    // ver AdvancedFilterPanel.vue). ids son el id nativo por proveedor: WFS
    // feature id (GeoServer), OBJECTID (ArcGIS), índice en entry.records
    // (importada) — ver attributesTable.js::fetchTargetFeaturesByIds.
    targetSelectionMode: "attribute",
    targetSelection: { ids: [] },
  },
});

export const cloneFilter = value => JSON.parse(JSON.stringify(value));

const normalizedCondition = condition => {
  if (condition && typeof condition === "object" && condition.operator) return condition;
  return { operator: "contains", value: condition == null ? "" : String(condition) };
};

export const isConditionActive = condition => {
  const item = normalizedCondition(condition);
  if (!item.field && condition && condition.kind === "condition") return false;
  if (item.operator === "isNull" || item.operator === "isNotNull") return true;
  if (item.operator === "in") return Array.isArray(item.value) && item.value.length > 0;
  return item.value !== null && item.value !== undefined && String(item.value).trim() !== "";
};

export const hasAttributePredicate = node => {
  if (!node) return false;
  if (node.kind === "condition") return !!node.field && isConditionActive(node);
  return (node.children || []).some(hasAttributePredicate);
};

export const hasAppliedPredicate = filter => {
  if (!filter || filter.mode === "none") return false;
  if (filter.mode === "quick") {
    return Object.values(filter.quick || {}).some(isConditionActive);
  }
  const advanced = filter.advanced || {};
  return !!(
    (advanced.attribute && advanced.attribute.enabled && hasAttributePredicate(advanced.attribute.root)) ||
    (advanced.roi && advanced.roi.enabled && advanced.roi.spec) ||
    (advanced.crossLayer && advanced.crossLayer.enabled && advanced.crossLayer.targetLayerId && advanced.crossLayer.operation)
  );
};

export const migrateAppliedFilter = filter => {
  if (!filter || !["none", "quick", "advanced"].includes(filter.mode)) return emptyAppliedFilter();
  const migrated = cloneFilter(filter);
  migrated.version = FILTER_VERSION;
  if (migrated.mode === "advanced") {
    migrated.advanced = { ...emptyAdvancedDraft(), ...(migrated.advanced || {}) };
    migrated.advanced.crossLayer = {
      ...emptyAdvancedDraft().crossLayer,
      ...((migrated.advanced && migrated.advanced.crossLayer) || {}),
    };
  }
  return migrated;
};

export const validateAttributeTree = (node, schema = {}, limits = {}) => {
  const errors = [];
  const counters = { groups: 0, conditions: 0 };
  const maxGroups = limits.maxGroups || 20;
  const maxConditions = limits.maxConditions || 50;

  const visit = (item, depth) => {
    if (!item) return;
    if (item.kind === "group") {
      counters.groups += 1;
      if (depth > 3) errors.push("La profundidad máxima es de tres niveles");
      (item.children || []).forEach(child => visit(child, depth + (child.kind === "group" ? 1 : 0)));
      return;
    }
    counters.conditions += 1;
    if (!item.field || !Object.prototype.hasOwnProperty.call(schema, item.field)) {
      errors.push("Hay una condición sin atributo válido");
      return;
    }
    if (item.operator === "in" && (!Array.isArray(item.value) || item.value.length > 50)) {
      errors.push(`El atributo ${item.field} debe tener entre 1 y 50 valores`);
    }
    if (!isConditionActive(item)) errors.push(`Falta un valor para ${item.field}`);
    if (schema[item.field] === "number") {
      const values = item.operator === "in" ? item.value : [item.value];
      if (!["isNull", "isNotNull"].includes(item.operator) && values.some(v => !Number.isFinite(Number(v)))) {
        errors.push(`El valor de ${item.field} debe ser numérico`);
      }
    }
  };

  visit(node, 1);
  if (counters.groups > maxGroups) errors.push(`Máximo ${maxGroups} grupos`);
  if (counters.conditions > maxConditions) errors.push(`Máximo ${maxConditions} condiciones`);
  return Array.from(new Set(errors));
};

const escapeSqlString = value => String(value).replace(/'/g, "''");

const literal = (value, type, dialect) => {
  if (type === "number") return String(Number(value));
  if (type === "boolean") {
    const truthy = value === true || value === "true" || value === 1 || value === "1";
    return dialect === "geoserver" ? (truthy ? "TRUE" : "FALSE") : (truthy ? "1" : "0");
  }
  if (type === "date") {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) throw new Error(`Fecha inválida: ${value}`);
    const text = date.toISOString().replace("T", " ").replace(/\.\d{3}Z$/, "");
    return dialect === "arcgis" ? `DATE '${text}'` : `'${text}'`;
  }
  return `'${escapeSqlString(value)}'`;
};

// GeoServer's Mongo datastore flattens nested documents into dotted property
// names (p.ej. "propertiesPublic.campo_mrytlu71_2", el esquema real de los
// campos dinámicos de Capa_Terreno). ECQL trata un "." sin comillas en un
// nombre de propiedad como navegación anidada ("Illegal property name"), así
// que hay que citar el identificador — confirmado empírico contra GeoServer.
const qualifyField = (field, dialect) => (dialect === "geoserver" ? `"${field}"` : field);

const compileCondition = (condition, dialect, schema, fields) => {
  if (!condition.field || !fields.has(condition.field)) throw new Error("Atributo no permitido");
  const field = qualifyField(condition.field, dialect);
  const type = schema[condition.field] || "text";
  const operator = condition.operator;
  if (operator === "isNull") return `${field} IS NULL`;
  if (operator === "isNotNull") return `${field} IS NOT NULL`;
  if (operator === "in") return `${field} IN (${condition.value.map(v => literal(v, type, dialect)).join(", ")})`;

  if (["contains", "like", "ilike"].includes(operator)) {
    const pattern = operator === "contains"
      ? `%${escapeSqlString(condition.value)}%`
      : escapeSqlString(condition.value);
    if (dialect === "geoserver") {
      const keyword = operator === "like" ? "LIKE" : "ILIKE";
      return `${field} ${keyword} '${pattern}'`;
    }
    if (operator === "like") return `${field} LIKE '${pattern}'`;
    return `UPPER(${field}) LIKE UPPER('${pattern}')`;
  }

  const operators = { eq: "=", neq: "<>", gt: ">", gte: ">=", lt: "<", lte: "<=" };
  return `${field} ${operators[operator] || "="} ${literal(condition.value, type, dialect)}`;
};

export const compileAttributeTree = (node, dialect, schema = {}) => {
  const fields = new Set(Object.keys(schema));
  const compile = item => {
    if (item.kind === "condition") return compileCondition(item, dialect, schema, fields);
    const clauses = (item.children || []).filter(hasAttributePredicate).map(compile);
    if (!clauses.length) return null;
    const joined = clauses.map(clause => `(${clause})`).join(item.match === "all" ? " AND " : " OR ");
    return item.match === "none" ? `NOT (${joined})` : `(${joined})`;
  };
  return node && hasAttributePredicate(node) ? compile(node) : null;
};

export const compileQuickFilters = (filters, dialect, schema = {}) => {
  const conditions = Object.entries(filters || {})
    .filter(([, condition]) => isConditionActive(condition))
    .map(([field, condition]) => ({ ...normalizedCondition(condition), kind: "condition", field }));
  if (!conditions.length) return null;
  return compileAttributeTree({ kind: "group", match: "all", children: conditions }, dialect, schema);
};

export const circleVertices = (center, radiusMeters, segments = 64) => {
  const [lon, lat] = center.map(Number);
  const angularDistance = radiusMeters / EARTH_RADIUS;
  const lat1 = lat * Math.PI / 180;
  const lon1 = lon * Math.PI / 180;
  const points = [];
  for (let index = 0; index < segments; index += 1) {
    const bearing = (index / segments) * Math.PI * 2;
    const lat2 = Math.asin(
      Math.sin(lat1) * Math.cos(angularDistance) +
      Math.cos(lat1) * Math.sin(angularDistance) * Math.cos(bearing)
    );
    const lon2 = lon1 + Math.atan2(
      Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(lat1),
      Math.cos(angularDistance) - Math.sin(lat1) * Math.sin(lat2)
    );
    points.push([((lon2 * 180 / Math.PI + 540) % 360) - 180, lat2 * 180 / Math.PI]);
  }
  points.push(points[0].slice());
  return points;
};

export const roiPolygon4326 = spec => {
  if (!spec || !spec.shape) return null;
  const shape = spec.shape;
  if (shape.kind === "circle") return [circleVertices(shape.center, Number(shape.radiusMeters), shape.segments || 64)];
  if (shape.kind === "rectangle" || shape.kind === "viewport") {
    const [west, south, east, north] = shape.bbox.map(Number);
    return [[[west, south], [east, south], [east, north], [west, north], [west, south]]];
  }
  if (shape.kind === "polygon") {
    const vertices = (shape.vertices || []).map(point => point.map(Number));
    if (vertices.length && (vertices[0][0] !== vertices[vertices.length - 1][0] || vertices[0][1] !== vertices[vertices.length - 1][1])) {
      vertices.push(vertices[0].slice());
    }
    return [vertices];
  }
  return null;
};

export const arcgisSpatialRelationship = operation => ({
  intersects: "esriSpatialRelIntersects",
  featureWithinArea: "esriSpatialRelWithin",
  featureContainsArea: "esriSpatialRelContains",
}[operation] || "esriSpatialRelIntersects");

export const geoserverSpatialPredicate = operation => ({
  intersects: "INTERSECTS",
  featureWithinArea: "WITHIN",
  featureContainsArea: "CONTAINS",
  sourceWithinTarget: "WITHIN",
  sourceContainsTarget: "CONTAINS",
}[operation] || "INTERSECTS");

export const arcgisCrossLayerRelationship = operation => ({
  intersects: "esriSpatialRelIntersects",
  sourceWithinTarget: "esriSpatialRelWithin",
  sourceContainsTarget: "esriSpatialRelContains",
}[operation] || "esriSpatialRelIntersects");

const OPERATOR_DESCRIPTION_LABELS = {
  eq: "es igual a",
  neq: "es distinto de",
  gt: "es mayor que",
  gte: "es mayor o igual que",
  lt: "es menor que",
  lte: "es menor o igual que",
  contains: "contiene",
  like: "coincide con",
  ilike: "coincide con",
  in: "está en",
  isNull: "es nulo",
  isNotNull: "no es nulo",
};

const MATCH_DESCRIPTION_LABELS = { any: "alguna", all: "todas", none: "ninguna" };

const ROI_SHAPE_DESCRIPTION_LABELS = { rectangle: "Rectángulo", viewport: "Vista actual", circle: "Círculo", polygon: "Polígono" };

const ROI_OPERATION_DESCRIPTION_LABELS = {
  intersects: "Intersecta",
  featureWithinArea: "La entidad está dentro del área",
  featureContainsArea: "La entidad contiene el área",
};

const CROSS_LAYER_OPERATION_DESCRIPTION_LABELS = {
  intersects: "Intersecta la capa objetivo",
  sourceWithinTarget: "Está contenida en la capa objetivo",
  sourceContainsTarget: "Contiene la capa objetivo",
};

const describeCondition = (condition, aliases) => {
  const field = aliases[condition.field] || condition.field;
  const opLabel = OPERATOR_DESCRIPTION_LABELS[condition.operator] || condition.operator;
  if (condition.operator === "isNull" || condition.operator === "isNotNull") return `${field} ${opLabel}`;
  const value = Array.isArray(condition.value) ? condition.value.join(", ") : condition.value;
  return `${field} ${opLabel} "${value}"`;
};

const describeAttributeNode = (node, aliases, depth = 0) => {
  if (!node || !hasAttributePredicate(node)) return [];
  if (node.kind === "condition") return [describeCondition(node, aliases)];
  const childLines = (node.children || []).filter(hasAttributePredicate).flatMap(child => describeAttributeNode(child, aliases, depth + 1));
  if (!childLines.length) return [];
  const indent = "  ".repeat(depth);
  const matchLabel = MATCH_DESCRIPTION_LABELS[node.match] || node.match;
  return [`${indent}Cumple ${matchLabel} de estas condiciones:`, ...childLines.map(line => `${indent}  ${line}`)];
};

const describeRoi = spec => {
  if (!spec) return [];
  const opLabel = ROI_OPERATION_DESCRIPTION_LABELS[spec.operation] || spec.operation;
  const shapeLabel = spec.shape ? (ROI_SHAPE_DESCRIPTION_LABELS[spec.shape.kind] || spec.shape.kind) : "área definida";
  return [`Área de interés (${shapeLabel}): ${opLabel}`];
};

const describeCrossLayer = (crossLayer, aliases) => {
  if (!crossLayer) return [];
  const layerName = crossLayer.targetLayerLabel || crossLayer.targetLayerId || "capa desconocida";
  const opLabel = CROSS_LAYER_OPERATION_DESCRIPTION_LABELS[crossLayer.operation] || crossLayer.operation;
  const lines = [`Filtro por capa "${layerName}": ${opLabel}`];
  if (crossLayer.targetSelectionMode === "click") {
    const count = ((crossLayer.targetSelection && crossLayer.targetSelection.ids) || []).length;
    lines.push(`  Selección manual de ${count} entidad(es) en el mapa`);
  } else if (crossLayer.targetAttribute && crossLayer.targetAttribute.enabled) {
    lines.push(...describeAttributeNode(crossLayer.targetAttribute.root, aliases, 1));
  }
  return lines;
};

// describeAppliedFilter: traduce un appliedFilter (quick/advanced) a líneas
// de texto legibles en español, para adjuntar como metadata en exportaciones
// (ver attributesTable.js::downloadFeatures). aliases es el diccionario
// campo técnico -> label (metadata.aliases de getLayerMetadata) — sin él,
// cae al nombre técnico de columna. Devuelve null si no hay nada activo que
// describir (mismo criterio que hasAppliedPredicate).
export const describeAppliedFilter = (filter, aliases = {}) => {
  if (!hasAppliedPredicate(filter)) return null;
  if (filter.mode === "quick") {
    const lines = Object.entries(filter.quick || {})
      .filter(([, condition]) => isConditionActive(condition))
      .map(([field, condition]) => describeCondition({ field, operator: condition.operator, value: condition.value }, aliases));
    return lines.length ? ["Filtro aplicado:", ...lines] : null;
  }
  const parts = activeAdvancedParts(filter);
  const lines = [];
  if (parts.attribute) lines.push(...describeAttributeNode(parts.attribute, aliases));
  if (parts.roi) lines.push(...describeRoi(parts.roi));
  if (parts.crossLayer) lines.push(...describeCrossLayer(parts.crossLayer, aliases));
  return lines.length ? ["Filtro aplicado:", ...lines] : null;
};

export const activeAdvancedParts = filter => {
  const advanced = filter && filter.mode === "advanced" ? filter.advanced : null;
  return {
    attribute: advanced && advanced.attribute && advanced.attribute.enabled && hasAttributePredicate(advanced.attribute.root)
      ? advanced.attribute.root : null,
    roi: advanced && advanced.roi && advanced.roi.enabled ? advanced.roi.spec : null,
    crossLayer: advanced && advanced.crossLayer && advanced.crossLayer.enabled && advanced.crossLayer.targetLayerId
      ? advanced.crossLayer : null,
  };
};
